import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAnterior, isPatientRight, isUpper, type Surface, surfaceLabel } from '@/utils/dental';

type Props = {
  tooth: number;
  value: Surface[];
  onChange: (value: Surface[]) => void;
};

/** Big five-surface diagram, oriented like the tooth on the chart. */
export function SurfacePicker({ tooth, value, onChange }: Props) {
  const { t } = useTranslation();
  const upper = isUpper(tooth);
  const right = isPatientRight(tooth);
  const centre: Surface = isAnterior(tooth) ? 'I' : 'O';
  const regions: { surface: Surface; d?: string; rect?: true; lx: number; ly: number }[] = [
    { surface: upper ? 'B' : 'L', d: 'M4,4 L164,4 L116,52 L52,52 Z', lx: 84, ly: 32 },
    { surface: upper ? 'L' : 'B', d: 'M4,164 L164,164 L116,116 L52,116 Z', lx: 84, ly: 146 },
    { surface: right ? 'D' : 'M', d: 'M4,4 L52,52 L52,116 L4,164 Z', lx: 24, ly: 89 },
    { surface: right ? 'M' : 'D', d: 'M164,4 L116,52 L116,116 L164,164 Z', lx: 144, ly: 89 },
    { surface: centre, rect: true, lx: 84, ly: 89 },
  ];
  const toggle = (s: Surface) =>
    onChange(value.includes(s) ? value.filter((x) => x !== s) : [...value, s]);

  return (
    // biome-ignore lint/a11y/useSemanticElements: an SVG drawing that groups tooth buttons
    <svg
      className="surface-picker"
      viewBox="0 0 168 168"
      role="group"
      aria-label={t('chart.surfaces')}
    >
      {regions.map((r) => {
        const pressed = value.includes(r.surface);
        const common = {
          role: 'button',
          tabIndex: 0,
          'aria-pressed': pressed,
          'aria-label': surfaceLabel(r.surface),
          onClick: () => toggle(r.surface),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggle(r.surface);
            }
          },
        };
        return (
          <g key={r.surface}>
            {r.rect ? (
              <rect x={52} y={52} width={64} height={64} {...common} />
            ) : (
              <path d={r.d} {...common} />
            )}
            <text x={r.lx} y={r.ly} textAnchor="middle">
              {r.surface}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
